import { useQuery, useMutation } from '@tanstack/react-query'

import { convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type { LeadListWithPagesProps, LeadProps } from '@/types'

import { LEADS_URL, LEAD_URL } from './api.urls'

export const useLeadList = (params: Record<string, string | number>) => {
  const getLeadList = async (): Promise<{
    success: boolean
    message: string
    data?: LeadListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(LEADS_URL, {
        params: {
          ...(convertObjectToSnakeCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Lead requests fetched successfully!',
        data: convertObjectToCamelCase(data) as LeadListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get lead requests!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.LEAD.module, params],
    queryFn: () => getLeadList(),
  })
}

export const useLeadDetail = (uuid?: string) => {
  const getLeadDetail = async (): Promise<{
    success: boolean
    message: string
    data?: LeadProps
  }> => {
    try {
      const { data } = await api.get(LEAD_URL(`${uuid}`))

      return {
        success: true,
        message: 'Lead request fetched successfully!',
        data: convertObjectToCamelCase(data) as LeadProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get lead request!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.LEAD.module, uuid],
    queryFn: () => getLeadDetail(),
    enabled: !!uuid,
  })
}

export const useLeadUpdate = (uuid?: string) => {
  const updateLead = async (
    payload: Partial<LeadProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: LeadProps
  }> => {
    try {
      const { data } = await api.patch(
        LEAD_URL(`${uuid}`),
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Lead updated successfully!',
        data: convertObjectToCamelCase(data) as LeadProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update lead!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.LEAD.module, uuid],
    mutationFn: (payload: Partial<LeadProps>) => updateLead(payload),
  })
}

export const useDeleteLead = () => {
  const deleteLead = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: undefined
  }> => {
    try {
      await api.delete(LEAD_URL(`${uuid}`))

      return {
        success: true,
        message: 'Lead deleted successfully!',
        data: undefined,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to delete lead!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.LEAD.module],
    mutationFn: (uuid: string) => deleteLead(uuid),
  })
}
